import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import { isTokenExpired } from "lib/utils/verifyJwt";
import { store } from "lib/redux/store";
import { setAccessToken } from "lib/redux/uiSlice";

// Create an Axios instance
const axiosClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "*/*",
  },
});

// Request interceptor to add Authorization token
axiosClient.interceptors.request.use(
  (config) => {
    // Skip token validation for authentication endpoints
    // These endpoints don't need a token (they provide one!)
    const authEndpoints = ['/auth/google-one-tap', '/auth/'];
    const isAuthEndpoint = authEndpoints.some(endpoint => config.url?.includes(endpoint));
    
    if (isAuthEndpoint) {
      // Don't add Authorization header and don't check token
      return config;
    }
    
    const token = localStorage.getItem("access-token");
    if (!token || isTokenExpired(token)) {
      localStorage.removeItem("access-token");
      store.dispatch(setAccessToken(null));
      return Promise.reject(
        new AxiosError("Token expired. Please log in again.", "TOKEN_EXPIRED")
      );
    } else {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
axiosClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response;

      // You can customize this based on error status codes
      switch (status) {
        case 400:
          console.error("Bad request:", data);
          break;
        case 401:
          // Show a toast notification
          toast.error("Session expired. Please log in again.");
          // Clear token and user state
          localStorage.removeItem("access-token");
          store.dispatch(setAccessToken(null));
          // Redirect to login page after a short delay to allow toast to show
          setTimeout(() => {
            window.location.href = "/";
          }, 1200);
          break;
        case 404:
          console.error("Resource not found:", data);
          break;
        case 500:
          console.error("Server error:", data);
          break;
        default:
          console.error("Unhandled error:", data);
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Request setup error:", error.message);
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
